import React, { useState, useEffect } from 'react';
import { Download, CheckCircle, XCircle, Edit3, Trash2, ChevronRight, Package, RefreshCw, AlertTriangle, RotateCcw, CloudDownload } from 'lucide-react';
import { syncService } from '../services/api';
import ProductEditor from './ProductEditor';

const StagingList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [filter, setFilter] = useState('ALL');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stats, setStats] = useState({ byStatus: {}, byValidation: {} });
  const [wooCount, setWooCount] = useState(0);
  const [stagedCount, setStagedCount] = useState(0);

  useEffect(() => {
    fetchProducts();
    fetchStats();
    fetchWooCount();
  }, [filter]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const status = filter === 'ALL' ? null : filter;
      const response = await syncService.getStagedProducts(status);
      setProducts(response.data.products || []);
      setStagedCount(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await syncService.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchWooCount = async () => {
    try {
      const response = await syncService.getWooCommerceCount();
      setWooCount(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch WooCommerce count:', error);
    }
  };

  const handleImport = async () => {
    try {
      setImporting(true);
      const response = await syncService.importProducts(50, 1);
      alert(`Imported ${response.data.imported} products, skipped ${response.data.skipped} existing`);
      fetchProducts();
      fetchStats();
      fetchWooCount();
    } catch (error) {
      console.error('Import failed:', error);
      alert('Import failed. Check console for details.');
    } finally {
      setImporting(false);
    }
  };

  const handleImportAll = async () => {
    const remaining = wooCount - stagedCount;
    if (remaining <= 0) {
      alert('All products are already imported!');
      return;
    }

    if (!window.confirm(`Import all ${remaining} remaining products from WooCommerce?\n\nThis may take a moment...`)) {
      return;
    }

    try {
      setImporting(true);
      const response = await syncService.importAllProducts();
      alert(`Imported ${response.data.imported} products, skipped ${response.data.skipped} existing`);
      fetchProducts();
      fetchStats();
      fetchWooCount();
    } catch (error) {
      console.error('Import all failed:', error);
      alert('Import failed. Check console for details.');
    } finally {
      setImporting(false);
    }
  };

  const handleSyncStatus = async () => {
    try {
      setSyncing(true);
      const response = await syncService.syncStatus();
      const { synced, pending } = response.data;
      if (synced > 0) {
        alert(`Synced ${synced} products to Published status.\n${pending} products still pending approval.`);
      } else {
        alert(`No status changes. ${pending} products still pending approval on Leroy Merlin.`);
      }
      fetchProducts();
      fetchStats();
    } catch (error) {
      console.error('Sync failed:', error);
      alert('Sync failed. Check console for details.');
    } finally {
      setSyncing(false);
    }
  };

  const handleResyncFromWoo = async () => {
    if (!window.confirm('Resync all staged products from WooCommerce?\n\nThis will update prices, descriptions, stock levels, and images from WooCommerce.')) {
      return;
    }

    try {
      setResyncing(true);
      const response = await syncService.resyncFromWooCommerce();
      alert(`Resynced ${response.data.updated} products from WooCommerce.${response.data.notFound > 0 ? `\n${response.data.notFound} products not found in WooCommerce.` : ''}`);
      fetchProducts();
      fetchStats();
    } catch (error) {
      console.error('Resync failed:', error);
      alert('Resync failed. Check console for details.');
    } finally {
      setResyncing(false);
    }
  };

  const handlePublish = async () => {
    const readyProducts = products.filter(
      (p) => p.stagingStatus === 'READY' && p.validationStatus === 'VALID'
    );

    if (readyProducts.length === 0) {
      alert('No products ready for publishing');
      return;
    }

    if (!window.confirm(`Submit ${readyProducts.length} products to Leroy Merlin for approval?`)) {
      return;
    }

    try {
      setPublishing(true);
      const ids = readyProducts.map((p) => p.id);
      const response = await syncService.submitForApproval(ids);
      alert(`Submitted: ${response.data.success}, Failed: ${response.data.failed}\nImport ID: ${response.data.importId}`);
      fetchProducts();
      fetchStats();
    } catch (error) {
      console.error('Submit failed:', error);
      alert('Submit failed. Check console for details.');
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this product from staging?')) return;

    try {
      await syncService.deleteStagedProduct(id);
      fetchProducts();
      fetchStats();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleRetry = async (id) => {
    try {
      await syncService.bulkUpdateStatus([id], 'IMPORTED');
      fetchProducts();
      fetchStats();
    } catch (error) {
      console.error('Retry failed:', error);
      alert('Failed to reset product status');
    }
  };

  const handleRetryAll = async () => {
    const failedProducts = products.filter((p) => p.stagingStatus === 'FAILED');
    if (failedProducts.length === 0) return;

    if (!window.confirm(`Reset ${failedProducts.length} failed products to Imported status?`)) return;

    try {
      const ids = failedProducts.map((p) => p.id);
      await syncService.bulkUpdateStatus(ids, 'IMPORTED');
      fetchProducts();
      fetchStats();
    } catch (error) {
      console.error('Retry all failed:', error);
      alert('Failed to reset products');
    }
  };

  const handleProductSaved = () => {
    setSelectedProduct(null);
    fetchProducts();
    fetchStats();
  };

  const getStatusBadge = (status) => {
    const badges = {
      IMPORTED: { class: 'badge-neutral', label: 'Imported' },
      REVIEWING: { class: 'badge-info', label: 'Reviewing' },
      READY: { class: 'badge-success', label: 'Ready' },
      PENDING_APPROVAL: { class: 'badge-warning', label: 'Pending' },
      PUBLISHING: { class: 'badge-info', label: 'Publishing' },
      PUBLISHED: { class: 'badge-success', label: 'Published' },
      FAILED: { class: 'badge-error', label: 'Failed' },
      EXCLUDED: { class: 'badge-neutral', label: 'Excluded' },
    };
    return badges[status] || badges.IMPORTED;
  };

  const readyCount = products.filter(
    (p) => p.stagingStatus === 'READY' && p.validationStatus === 'VALID'
  ).length;

  const totalStaged = Object.values(stats.byStatus || {}).reduce((a, b) => a + b, 0);
  const remainingToImport = wooCount - totalStaged;
  const pendingCount = stats.byStatus?.PENDING_APPROVAL || 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Product Staging</h1>
          <p className="text-secondary" style={{ marginTop: '4px' }}>
            Import from WooCommerce, review, and publish to Leroy Merlin
          </p>
          {wooCount > 0 && (
            <p style={{ marginTop: '8px', fontSize: '13px', color: '#6e6e73' }}>
              WooCommerce: <strong>{wooCount}</strong> products | 
              Staged: <strong>{totalStaged}</strong> | 
              Remaining: <strong>{remainingToImport > 0 ? remainingToImport : 0}</strong>
            </p>
          )}
        </div>
        <div className="page-header-actions">
          <button
            className="btn btn-secondary"
            onClick={handleResyncFromWoo}
            disabled={resyncing || totalStaged === 0}
            title="Pull latest changes from WooCommerce"
          >
            <RefreshCw size={16} className={resyncing ? 'spinning' : ''} />
            {resyncing ? 'Resyncing...' : 'Resync from WooCommerce'}
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={handleImportAll} 
            disabled={importing || remainingToImport <= 0}
            title={remainingToImport <= 0 ? 'All products imported' : `Import ${remainingToImport} products`}
          >
            <RefreshCw size={16} className={importing ? 'spinning' : ''} />
            {importing ? 'Importing...' : `Import All (${remainingToImport > 0 ? remainingToImport : 0})`}
          </button>
          {pendingCount > 0 && (
            <button 
              className="btn btn-secondary" 
              onClick={handleSyncStatus} 
              disabled={syncing}
              title="Check Leroy Merlin for approved products"
            >
              <CloudDownload size={16} className={syncing ? 'spinning' : ''} />
              {syncing ? 'Syncing...' : `Sync Status (${pendingCount})`}
            </button>
          )}
          <button className="btn btn-primary" onClick={handlePublish} disabled={publishing || readyCount === 0}>
            <ChevronRight size={16} />
            {publishing ? 'Submitting...' : `Submit to Leroy Merlin (${readyCount})`}
          </button>
        </div>
      </div>

      <div style={styles.filters}>
        <div className="tabs">
          {['ALL', 'IMPORTED', 'READY', 'PENDING_APPROVAL', 'PUBLISHED', 'FAILED', 'EXCLUDED'].map((status) => (
            <button
              key={status}
              className={`tab ${filter === status ? 'active' : ''}`}
              onClick={() => setFilter(status)}
            >
              {status === 'ALL' ? 'All' : status.charAt(0) + status.slice(1).toLowerCase().replace('_', ' ')}
              {status !== 'ALL' && stats.byStatus?.[status] && (
                <span style={styles.count}>{stats.byStatus[status]}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Pending Approval Info Banner */}
      {filter === 'PENDING_APPROVAL' && products.length > 0 && (
        <div style={styles.infoBanner}>
          <CloudDownload size={20} />
          <div style={styles.infoBannerText}>
            <strong>{products.length} product(s) awaiting approval on Leroy Merlin.</strong> Click "Sync Status" to check for approvals.
          </div>
          <button className="btn btn-secondary btn-sm" onClick={handleSyncStatus} disabled={syncing}>
            <CloudDownload size={14} />
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
      )}

      {/* Failed Products Warning Banner */}
      {filter === 'FAILED' && products.length > 0 && (
        <div style={styles.errorBanner}>
          <AlertTriangle size={20} />
          <div style={styles.errorBannerText}>
            <strong>{products.length} product(s) failed to submit.</strong> Review the errors below, fix the issues, and retry.
          </div>
          <button className="btn btn-secondary btn-sm" onClick={handleRetryAll}>
            <RotateCcw size={14} />
            Reset All to Imported
          </button>
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="loading">
            <div className="spinner" />
          </div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <Package size={48} />
            <h3>No products in staging</h3>
            <p>Import products from WooCommerce to get started</p>
            <button className="btn btn-primary" onClick={handleImportAll} disabled={importing}>
              <Download size={16} />
              Import All Products ({wooCount})
            </button>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th>Price</th>
                  <th>Qty</th>
                  <th>Validation</th>
                  <th>Status</th>
                  {filter === 'FAILED' && <th>Error</th>}
                  <th style={{ width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => {
                  const statusBadge = getStatusBadge(product.stagingStatus);
                  return (
                    <tr key={product.id}>
                      <td>
                        <div style={styles.productCell}>
                          {product.wooImages?.[0] && (
                            <img src={product.wooImages[0]} alt="" style={styles.productImage} />
                          )}
                          <div style={styles.productInfo}>
                            <span style={styles.productName}>
                              {product.marketplaceTitle || product.wooName}
                            </span>
                            <span style={styles.productSource}>
                              {product.wooConnection?.storeUrl?.replace('https://', '')}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span style={styles.sku}>{product.marketplaceSku || '—'}</span>
                      </td>
                      <td>R {Number(product.marketplacePrice || 0).toLocaleString()}</td>
                      <td>{product.marketplaceQuantity ?? '—'}</td>
                      <td>
                        {product.validationStatus === 'VALID' ? (
                          <span className="badge badge-success">
                            <CheckCircle size={12} />
                            Valid
                          </span>
                        ) : (
                          <span 
                            className="badge badge-error" 
                            title={product.validationErrors?.join('\n') || 'Validation failed'}
                            style={{ cursor: 'help' }}
                          >
                            <XCircle size={12} />
                            {product.validationErrors?.length || 0} issues
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${statusBadge.class}`}>{statusBadge.label}</span>
                      </td>
                      {filter === 'FAILED' && (
                        <td>
                          <span style={styles.errorText} title={product.publishError || 'Unknown error'}>
                            {product.publishError 
                              ? (product.publishError.length > 50 
                                  ? product.publishError.substring(0, 50) + '...' 
                                  : product.publishError)
                              : 'Unknown error'}
                          </span>
                        </td>
                      )}
                      <td>
                        <div style={styles.actions}>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setSelectedProduct(product)}
                            title="Edit"
                          >
                            <Edit3 size={16} />
                          </button>
                          {product.stagingStatus === 'FAILED' && (
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => handleRetry(product.id)}
                              title="Retry - Reset to Imported"
                              style={{ color: '#007aff' }}
                            >
                              <RotateCcw size={16} />
                            </button>
                          )}
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleDelete(product.id)}
                            title="Remove"
                            style={{ color: '#ff3b30' }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedProduct && (
        <ProductEditor
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onSave={handleProductSaved}
        />
      )}
    </div>
  );
};

const styles = {
  filters: {
    marginBottom: '20px',
  },
  count: {
    marginLeft: '6px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '600',
    background: 'rgba(0, 0, 0, 0.06)',
    borderRadius: '10px',
  },
  productCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  productImage: {
    width: '44px',
    height: '44px',
    borderRadius: '8px',
    objectFit: 'cover',
    background: '#f5f5f7',
  },
  productInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  productName: {
    fontWeight: '500',
    color: '#1d1d1f',
    maxWidth: '280px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  productSource: {
    fontSize: '12px',
    color: '#86868b',
    marginTop: '2px',
  },
  sku: {
    fontFamily: 'SF Mono, Monaco, Consolas, monospace',
    fontSize: '13px',
    color: '#6e6e73',
  },
  actions: {
    display: 'flex',
    gap: '4px',
  },
  infoBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    marginBottom: '20px',
    background: '#fffbeb',
    border: '1px solid #fef3c7',
    borderRadius: '8px',
    color: '#92400e',
  },
  infoBannerText: {
    flex: 1,
    fontSize: '14px',
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    marginBottom: '20px',
    background: '#fff5f5',
    border: '1px solid #fed7d7',
    borderRadius: '8px',
    color: '#c53030',
  },
  errorBannerText: {
    flex: 1,
    fontSize: '14px',
  },
  errorText: {
    fontSize: '12px',
    color: '#c53030',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'help',
  },
};

export default StagingList;